import React, { Fragment, useState, useCallback, useEffect, createContext, useContext, } from "react";
import { isObservable, observable, isObservableArray, extendObservable, has as observableHas, isObservableObject, } from "mobx";
import { Submit } from "./Submit";
import { Button } from "./Button";
import { Alert } from "./Alert";
import { SectionHeader } from "./SectionHeader";
import parseChildren from "./utils/parseChildren";
import { get, set } from "lodash";
import { observer } from "mobx-react-lite";
import { useTranslation } from "./hooks/useTranslation";
import { useUtilityStyles } from "./hooks/useUtilityStyles";
const FormContext = createContext(undefined);
export function Form({ children, values, onSubmit, disabled, loading, type = "raw", success, error, onCancel, title = "", failure, isStatic = false, id, element, styleOptions, }) {
    if (!isObservable(values)) {
        throw new Error("Form values must be observable");
    }
    return (React.createElement(FormContextProvider, { values: values, loading: loading, disabled: disabled, isStatic: isStatic },
        React.createElement(FormInternals, { onSubmit: onSubmit, type: type, success: success, error: error, onCancel: onCancel, title: title, failure: failure, id: id, element: element, styleOptions: styleOptions }, children)));
}
const FormAlerts = ({ success = false, form, error, failure = false, }) => {
    let hasFailure;
    if (typeof failure == "boolean") {
        hasFailure = {
            text: "Oops. Something has gone wrong",
            show: failure,
        };
    }
    if (typeof failure == "string") {
        hasFailure = {
            text: failure,
            show: !!failure,
        };
    }
    return (React.createElement(Fragment, null,
        error && form === "edit" && (React.createElement(Alert, { type: "warning", message: error, glyph: "circleAlert" })),
        hasFailure && hasFailure.show && (React.createElement(Alert, { type: "error", message: hasFailure.text, glyph: "removeSign" })),
        success && form !== "raw" && !error && (React.createElement(Alert, { type: "success", message: "Success", glyph: "circleCheck", overPage: true }))));
};
export const useFormContext = () => {
    const context = useContext(FormContext);
    if (context === undefined) {
        throw new Error("Input does not have a form");
    }
    return context;
};
function injectedGet(key) {
    return get(this, key);
}
function injectedSet(key, value) {
    set(this, key, value);
}
export function useFormContextValues() {
    const context = useFormContext();
    return context.values;
}
function useFormValuesImpl(initialValues = {}) {
    const [values] = useState(() => observable.object({
        ...initialValues,
        get: injectedGet,
        set: injectedSet,
    }));
    return values;
}
/** Combines useState with a mobx object observable
 *
 * For use with Forms Elements that can find fields by name from
 * the returns values from this hook.
 *
 * @deprecated Use react-hook-form's `useForm` instead (via the
 * `useModalForm` / `SimpleForm` helpers in tms-ux). The mobx-observable
 * form state this hook provides is being phased out in favour of
 * react-hook-form.
 *
 * @example
 * interface FormModel {
 *   firstName: string
 * }
 * // ...
 * const formValues = useTypedFormValues<FormModel>(initialFormValues);
 * // ...
 * <Input type="text" label="First Name" name="firstName" />
 */
export function useTypedFormValues(initialValues = {}) {
    return useFormValuesImpl(initialValues);
}
/** Do not use
 * @deprecated Use react-hook-form's `useForm` instead (via the
 * `useModalForm` / `SimpleForm` helpers in tms-ux).
 * @see useTypedFormValues
 */
export function useFormValues(initialValues = {}) {
    return useFormValuesImpl(initialValues);
}
export function FormContextProvider({ children, values, onSubmit, disabled, loading, isStatic, }) {
    const [isDirty, setIsDirty] = useState(false);
    const errors = useTypedFormValues();
    const formDisabled = disabled || loading;
    return (React.createElement(FormContext.Provider, { value: {
            values,
            onSubmit,
            errors,
            formDisabled,
            isDirty,
            setIsDirty,
            arrayIndex: 0,
            isStatic,
        } }, children));
}
function FormInternals({ children, onSubmit, type = "raw", success, error, onCancel, title = "", failure, id, element = "form", styleOptions, }) {
    const { utilities } = useUtilityStyles(styleOptions);
    const t = useTranslation();
    const { formDisabled, isDirty, setIsDirty, values } = useFormContext();
    const [form, setForm] = useState("view");
    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onSubmit)
            onSubmit(values);
        setIsDirty(false);
    }, [onSubmit, values, setIsDirty]);
    useEffect(() => {
        if (success)
            setForm("view");
        if (error)
            setForm("edit");
    }, [success, error]);
    const resetForm = useCallback(() => {
        if (type === "detail") {
            setForm("view");
        }
        else if (type === "live") {
            setIsDirty(false);
        }
        if (onCancel)
            onCancel();
    }, [onCancel, type, setIsDirty]);
    const formChildren = parseChildren(children);
    const viewChildren = formChildren.findByType(FormView);
    const editChildren = formChildren.findByType(FormEdit);
    const FormHTMLElement = element ?? "form";
    return (React.createElement(FormHTMLElement, { autoComplete: element === "form" ? "off" : undefined, onSubmit: handleSubmit, id: id, className: utilities },
        type === "live" && (React.createElement(Fragment, null,
            React.createElement(SectionHeader, { title: title },
                isDirty && (React.createElement("div", { className: "inline-message" },
                    React.createElement("span", { className: "icon glyphicon-pro glyphicon-pro-circle-alert" }),
                    "There are changes on this form")),
                React.createElement(Button, { label: t("Revert Changes"), disabled: formDisabled || !isDirty, onClick: () => resetForm(), id: id && `${id}__revert` }),
                React.createElement(Submit, { inline: true, isDirty: isDirty, id: id && `${id}__submit` })),
            React.createElement(FormAlerts, { form: form, success: success, error: error, failure: failure }),
            editChildren || children)),
        type === "detail" && (React.createElement(Fragment, null,
            React.createElement(SectionHeader, { title: title },
                form === "edit" && (React.createElement(Fragment, null,
                    React.createElement(Button, { label: t("Cancel"), onClick: () => resetForm(), id: id && `${id}__cancel` }),
                    React.createElement(Submit, { inline: true, isDirty: isDirty, id: id && `${id}__submit` }))),
                form === "view" && (React.createElement(Button, { label: t("Edit"), type: "primary", onClick: () => setForm("edit"), id: id && `${id}__edit` }))),
            React.createElement(FormAlerts, { form: form, success: success, error: error, failure: failure }),
            form === "edit" && editChildren,
            form === "view" && viewChildren)),
        type === "raw" && (React.createElement(Fragment, null,
            React.createElement(FormAlerts, { form: form, success: success, error: error, failure: failure }),
            children))));
}
export const FormView = ({ children, }) => {
    return React.createElement(Fragment, null, children);
};
export const FormEdit = ({ children, }) => {
    return React.createElement(Fragment, null, children);
};
const ensureObservableProp = (obj, key, template) => {
    if (!observableHas(obj, key)) {
        extendObservable(obj, { [key]: template });
    }
};
const useEnsureObservableProp = (values, name, defaultValue) => {
    const [loading, setLoading] = React.useState(true);
    useEffect(() => {
        ensureObservableProp(values, name, defaultValue);
        setLoading(false);
    }, [values, name, loading, setLoading, defaultValue]);
    return loading;
};
const injectLodashWorkaround = (value) => {
    // workaround for lodash access method
    value.get = injectedGet;
    value.set = injectedSet;
};
let ERROR_ID_COUNTER = 0;
export const FormObject = ({ children, name, }) => {
    const { values: parentValues, errors: parentErrors, arrayIndex, ...parentContext } = useFormContext();
    if (useEnsureObservableProp(parentValues, name, {}))
        return null;
    const values = parentValues[name];
    if (!isObservableObject(values)) {
        throw new Error("FormObject values must be observable object");
    }
    injectLodashWorkaround(values);
    return (React.createElement(FormElement, { arrayIndex: arrayIndex, values: values, parentErrors: parentErrors, parentContext: parentContext }, children));
};
export const FormArray = observer(({ children, name }) => {
    const { values: parentValues, errors: parentErrors, arrayIndex: _, ...parentContext } = useFormContext();
    if (useEnsureObservableProp(parentValues, name, []))
        return null;
    const entries = parentValues[name];
    if (!isObservableArray(entries)) {
        throw new Error("FormArray values must be observable array");
    }
    return (React.createElement(Fragment, null, entries.map((values, i) => {
        return (React.createElement(FormElement, { key: i, arrayIndex: i, values: values, parentErrors: parentErrors, parentContext: parentContext }, children));
    })));
});
export const FormElement = ({ children, values, arrayIndex, parentContext, parentErrors }) => {
    const [errorId] = React.useState(() => `sf${ERROR_ID_COUNTER++}`);
    if (useEnsureObservableProp(parentErrors, errorId, {}))
        return null;
    const errors = parentErrors[errorId];
    injectLodashWorkaround(values);
    return (React.createElement(FormContext.Provider, { value: {
            ...parentContext,
            values,
            errors,
            arrayIndex,
        } }, children));
};
export const FormConsumer = ({ children }) => {
    const context = useFormContext();
    return children(context);
};
export const FormActions = ({ children }) => {
    return React.createElement("div", { className: "form--actions" }, children);
};
